// apps/web/src/components/threat-actors/ThreatActorHighlightsBand.tsx
//
// KAN-221 — "What to watch": evidence-led editorial cells. The first two
// are corpus-wide (from /kpis); the last is explicitly a LOADED-VIEW read
// (derived from the capped list) and are labelled as such — no invented
// certainty, no fabricated heuristics. Cells degrade to conservative copy when
// their backing data is missing.

import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import type { ThreatActor, ThreatActorKpis } from '@/lib/types'
import {
  attributionLabel,
  kpiTotal,
  referenceRichCount,
} from './threat-actor-utils'

const TONE = {
  criminal: 'text-severity-high',
  state: 'text-primary',
  caution: 'text-warning',
  accent: 'text-primary',
  neutral: 'text-muted-foreground',
} as const

type Tone = keyof typeof TONE

function Cell(props: { tone: Tone; kicker: string; text: ReactNode; meta: ReactNode }) {
  return (
    <div className="min-w-0 bg-background p-4 sm:p-5">
      <span
        className={cn(
          'flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.1em]',
          TONE[props.tone],
        )}
      >
        <span className={cn('h-1.5 w-1.5 rounded-sm bg-current')} aria-hidden="true" />
        {props.kicker}
      </span>
      <p className="mt-2 text-[13.5px] font-medium leading-snug text-foreground">{props.text}</p>
      <p className="mt-1.5 text-[11.5px] text-muted-foreground">{props.meta}</p>
    </div>
  )
}

export interface ThreatActorHighlightsBandProps {
  kpis?: ThreatActorKpis
  actors?: ThreatActor[]
}

export function ThreatActorHighlightsBand({ kpis, actors }: ThreatActorHighlightsBandProps) {
  const total = kpis ? kpiTotal(kpis) : 0
  const loaded = actors ?? []
  const loadedCount = loaded.length

  const refRich = referenceRichCount(loaded)
  const dominant = kpis?.dominantAttribution ?? null

  return (
    <div className="grid grid-cols-1 gap-px border-y border-border bg-border sm:grid-cols-3">
      <Cell
        tone={dominant === 'criminal' ? 'criminal' : 'accent'}
        kicker="Dominant class"
        text={
          kpis && dominant
            ? `${attributionLabel(dominant)} is the largest class — ${kpis.attributionCounts[dominant].toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')} tracked.`
            : 'Attribution mix is still being established.'
        }
        meta="Largest published-attribution class across the registry."
      />

      <Cell
        tone="state"
        kicker="State-sponsored class"
        text={
          kpis
            ? `${kpis.attributionCounts.state.toLocaleString('en-GB')} state-sponsored clusters under tracking.`
            : 'State-sponsored tracking unavailable.'
        }
        meta="Published attribution class across the registry."
      />

      <Cell
        tone="accent"
        kicker="Corroboration"
        text={
          loadedCount > 0
            ? `${refRich} of ${loadedCount} loaded dossiers carry MITRE, MISP or Malpedia IDs.`
            : 'Source corroboration unavailable for this view.'
        }
        meta="External identifiers present in the loaded view."
      />
    </div>
  )
}
